'use client';

import { c as _c } from "react/compiler-runtime";
import { ArrowUpRight, Bed, BywayFavorite, CoffeeCupSmall, MapPinAlt, NavArrowDown, StayPictogram } from '@byway/icons';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import { Fragment, useId, useState } from 'react';
import { Divider } from '~/components/Divider';
import { ListingCard } from '~/components/ListingCard';
import { AlternativeAccommodationDrawer } from '~/components/TripOverview/accommodationPage/AlternativeAccommodationDrawer';
import { BOARD_BASIS_ICON, ChangeRoomDrawer } from '~/components/TripOverview/accommodationPage/ChangeRoomDrawer';
import { usePrefetchAccommodationDrawersOnInView } from '~/components/TripOverview/accommodationPage/usePrefetchAccommodationDrawersOnInView';
import { useTripOverviewSummary } from '~/components/TripOverview/hooks/useTripOverviewSummary';
import { useIsTripUpdating } from '~/hooks/useIsTripUpdating';
import { api } from '~/libs/api';
import type { TripAccommodationStay } from '~/services/engine-ts';
import { expandedBoardBasis, isBywayFavouriteAccommodation } from '~/utils/accommodationHelpers';
import { trackFunnelEvent } from '~/utils/funnelTracking';
import styles from './AccommodationCard.module.css';
import type { AccommodationCardVariant } from './AccommodationCard.types';
import { AccommodationCheckInOutSection } from './AccommodationCheckInOutSection';
import { getAccommodationCheckInOutDetails } from './accommodationCheckInOut';
import { ItineraryCard } from './ItineraryCard';
type AccommodationDetails = {
  accommodation: {
    title: string;
    roomName: string;
    boardBasis?: string;
  };
};
const BODY_TRUNCATE_AT = 200;
type StayCheckInOutDetails = ReturnType<typeof getAccommodationCheckInOutDetails>;
const getPreviewBodyText = (bodyText: string) => {
  const slice = bodyText.slice(0, BODY_TRUNCATE_AT);
  const lastSpace = slice.lastIndexOf(' ');
  return `${bodyText.slice(0, lastSpace > 0 ? lastSpace : BODY_TRUNCATE_AT)}\u2026`;
};
type StayMetaBlockProps = {
  arrivalDistance: string | null;
  departureDistance: string | null;
  boardBases: string[];
};
function StayMetaBlock(t0) {
  const $ = _c(13);
  const {
    arrivalDistance,
    departureDistance,
    boardBases
  } = t0;
  const t = useTranslations("journey");
  if (!arrivalDistance && !departureDistance && boardBases.length === 0) {
    return null;
  }
  let t1;
  if ($[0] !== arrivalDistance) {
    t1 = arrivalDistance ? <div className={styles.metaRow}><span className={styles.metaIcon} aria-hidden={true}><MapPinAlt size="small" decorative={true} /></span><p className={styles.metaText}>{arrivalDistance}</p></div> : null;
    $[0] = arrivalDistance;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== departureDistance) {
    t2 = departureDistance ? <div className={styles.metaRow}><span className={styles.metaIcon} aria-hidden={true}><MapPinAlt size="small" decorative={true} /></span><p className={styles.metaText}>{departureDistance}</p></div> : null;
    $[2] = departureDistance;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== boardBases || $[5] !== t) {
    let t4;
    if ($[7] !== t) {
      t4 = boardBasis => {
        const Icon = boardBasis === "BB" ? CoffeeCupSmall : BOARD_BASIS_ICON[boardBasis as keyof typeof BOARD_BASIS_ICON] ?? Bed;
        return <div key={boardBasis} className={styles.metaRow}><span className={styles.metaIcon} aria-hidden={true}><Icon size="small" decorative={true} /></span><p className={styles.metaText}>{expandedBoardBasis(t, boardBasis)}</p></div>;
      };
      $[7] = t;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    t3 = boardBases.map(t4);
    $[4] = boardBases;
    $[5] = t;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[9] !== t1 || $[10] !== t2 || $[11] !== t3) {
    t4 = <div className={styles.metaBlock}>{t1}{t2}{t3}</div>;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
}
type StayBodySectionProps = {
  bodyText: string;
  isFoldExpanded: boolean;
  bodyOverflowId: string;
};
function StayBodySection(t0) {
  const $ = _c(24);
  const {
    bodyText,
    isFoldExpanded,
    bodyOverflowId
  } = t0;
  if (!bodyText) {
    return null;
  }
  if (bodyText.length <= BODY_TRUNCATE_AT) {
    let t1;
    if ($[0] !== bodyText) {
      t1 = <div className={styles.bodySection}><ItineraryCard.Body text={bodyText} disableTruncate={true} /></div>;
      $[0] = bodyText;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  const t1 = !isFoldExpanded && styles.foldExpandableOpen;
  let t2;
  if ($[2] !== t1) {
    t2 = clsx(styles.foldExpandable, t1);
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== bodyText) {
    t3 = getPreviewBodyText(bodyText);
    $[4] = bodyText;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t3) {
    t4 = <div className={styles.foldExpandableInner}><ItineraryCard.Body text={t3} disableTruncate={true} /></div>;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== isFoldExpanded || $[9] !== t2 || $[10] !== t4) {
    t5 = <div className={t2} inert={isFoldExpanded}>{t4}</div>;
    $[8] = isFoldExpanded;
    $[9] = t2;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const t6 = isFoldExpanded && styles.foldExpandableOpen;
  let t7;
  if ($[12] !== t6) {
    t7 = clsx(styles.foldExpandable, t6);
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const t8 = !isFoldExpanded;
  let t9;
  if ($[14] !== bodyText) {
    t9 = <div className={styles.foldExpandableInner}><ItineraryCard.Body text={bodyText} disableTruncate={true} /></div>;
    $[14] = bodyText;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== bodyOverflowId || $[17] !== t7 || $[18] !== t8 || $[19] !== t9) {
    t10 = <div id={bodyOverflowId} className={t7} inert={t8}>{t9}</div>;
    $[16] = bodyOverflowId;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t10 || $[22] !== t5) {
    t11 = <div className={styles.bodySection}>{t5}{t10}</div>;
    $[21] = t10;
    $[22] = t5;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  return t11;
}
type StayRestSectionProps = {
  websiteUrl: string | undefined;
  checkInOutDetails: StayCheckInOutDetails;
  isFoldExpanded: boolean;
  restOverflowId: string;
};
function StayRestSection(t0) {
  const $ = _c(15);
  const {
    websiteUrl,
    checkInOutDetails,
    isFoldExpanded,
    restOverflowId
  } = t0;
  const t = useTranslations("journey");
  if (!websiteUrl && !checkInOutDetails) {
    return null;
  }
  const t1 = isFoldExpanded && styles.foldExpandableOpen;
  let t2;
  if ($[0] !== t1) {
    t2 = clsx(styles.foldExpandable, t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = !isFoldExpanded;
  let t4;
  if ($[2] !== t || $[3] !== websiteUrl) {
    t4 = websiteUrl ? <a className={styles.externalLink} href={websiteUrl} target="_blank" rel="noopener noreferrer">{t("accommodation_website_link")}<ArrowUpRight size="small" decorative={true} /></a> : null;
    $[2] = t;
    $[3] = websiteUrl;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== checkInOutDetails) {
    t5 = checkInOutDetails ? <AccommodationCheckInOutSection details={checkInOutDetails} /> : null;
    $[5] = checkInOutDetails;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t4 || $[8] !== t5) {
    t6 = <div className={styles.foldExpandableInner}>{t4}{t5}</div>;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== restOverflowId || $[11] !== t2 || $[12] !== t3 || $[13] !== t6) {
    t7 = <div id={restOverflowId} className={t2} inert={t3}>{t6}</div>;
    $[10] = restOverflowId;
    $[11] = t2;
    $[12] = t3;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  return t7;
}
type StayFoldProps = {
  bodyText: string;
  websiteUrl: string | undefined;
  checkInOutDetails: StayCheckInOutDetails;
  defaultExpanded: boolean;
};
function StayFold({
  bodyText,
  websiteUrl,
  checkInOutDetails,
  defaultExpanded
}: Readonly<StayFoldProps>) {
  const tCommon = useTranslations('common');
  const bodyOverflowId = useId();
  const restOverflowId = useId();
  const [isFoldExpanded, setIsFoldExpanded] = useState(defaultExpanded);
  const isBodyTruncatable = Boolean(bodyText) && bodyText.length > BODY_TRUNCATE_AT;
  const hasRestContent = Boolean(websiteUrl || checkInOutDetails);
  const hasFoldableContent = isBodyTruncatable || hasRestContent;
  const ariaControls = [isBodyTruncatable ? bodyOverflowId : null, hasRestContent ? restOverflowId : null].filter(Boolean).join(' ') || undefined;
  return <>
      <div className={styles.foldGroup}>
        <StayBodySection bodyText={bodyText} isFoldExpanded={isFoldExpanded} bodyOverflowId={bodyOverflowId} />
        <StayRestSection websiteUrl={websiteUrl} checkInOutDetails={checkInOutDetails} isFoldExpanded={isFoldExpanded} restOverflowId={restOverflowId} />
      </div>

      {hasFoldableContent ? <button type="button" className={styles.textLinkButton} aria-expanded={isFoldExpanded} aria-controls={ariaControls} onClick={() => setIsFoldExpanded(open => !open)}>
          {isFoldExpanded ? tCommon('read_less') : tCommon('read_more')}
          <span className={clsx(styles.textLinkButtonIcon, isFoldExpanded && styles.textLinkButtonIconExpanded)} aria-hidden>
            <NavArrowDown size="small" decorative />
          </span>
        </button> : null}
    </>;
}
type AccommodationCardStayContentProps = {
  placeTitle: string;
  images: Parameters<typeof ItineraryCard.Gallery>[0]['images'];
  variant: AccommodationCardVariant;
  accommodationTitle: string | undefined;
  rooms: Array<{
    id: string;
    displayName: string;
  }>;
  accommodationDetails: AccommodationDetails | null;
  bodyText: string;
  accommodationStay?: TripAccommodationStay;
  arrivalDistance: string | null;
  departureDistance: string | null;
  boardBases: string[];
  tripId: string;
  itemId: number;
};
export function AccommodationCardStayContent(t0) {
  const $ = _c(94);
  const {
    placeTitle,
    images,
    variant,
    accommodationTitle,
    rooms,
    accommodationDetails,
    bodyText,
    accommodationStay,
    arrivalDistance,
    departureDistance,
    boardBases,
    tripId,
    itemId
  } = t0;
  const t = useTranslations("journey");
  const tFeedback = useTranslations("content.feedback");
  const [isAlternativesOpen, setIsAlternativesOpen] = useState(false);
  const [roomDrawerIndex, setRoomDrawerIndex] = useState(null);
  let t1;
  if ($[0] !== itemId || $[1] !== tripId) {
    t1 = {
      tripId,
      itemId
    };
    $[0] = itemId;
    $[1] = tripId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    ref: prefetchRef,
    prefetchRooms
  } = usePrefetchAccommodationDrawersOnInView(t1);
  const {
    canEditTripSelections
  } = useTripOverviewSummary();
  const isTripUpdating = useIsTripUpdating();
  const websiteUrl = accommodationStay?.accommodation.websiteUrl;
  let t2;
  if ($[3] !== accommodationStay) {
    t2 = getAccommodationCheckInOutDetails(accommodationStay);
    $[3] = accommodationStay;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const checkInOutDetails = t2;
  const t3 = accommodationStay?.accommodation.ranking ?? 0;
  let t4;
  if ($[5] !== t3) {
    t4 = isBywayFavouriteAccommodation(t3);
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const isFavourite = t4;
  const accommodationValue = accommodationDetails?.accommodation.title ?? accommodationTitle ?? "";
  const hasMultipleRooms = rooms.length > 1;
  let t5;
  if ($[7] !== tripId) {
    t5 = {
      tripId,
      replaceUnavailable: false
    };
    $[7] = tripId;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      enabled: false
    };
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const {
    data: liveCheck
  } = api.trip.liveCheck.useQuery(t5, t6);
  let t7;
  if ($[10] !== itemId || $[11] !== liveCheck?.liveCheckOutcome.stays) {
    t7 = liveCheck?.liveCheckOutcome.stays.some(stay => stay.itemId === itemId && stay.availabilityStatus === "UNAVAILABLE") ?? false;
    $[10] = itemId;
    $[11] = liveCheck?.liveCheckOutcome.stays;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const isStayUnavailable = t7;
  let t8;
  if ($[13] !== t) {
    t8 = t("includes_accommodation");
    $[13] = t;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== canEditTripSelections) {
    t9 = canEditTripSelections ? () => {
      trackFunnelEvent("trip_funnel_find_more_hotels");
      setIsAlternativesOpen(true);
    } : undefined;
    $[15] = canEditTripSelections;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== accommodationValue || $[18] !== isTripUpdating || $[19] !== t8 || $[20] !== t9) {
    t10 = <ItineraryCard.ChangeField label={t8} value={accommodationValue} onPress={t9} isDisabled={isTripUpdating} />;
    $[17] = accommodationValue;
    $[18] = isTripUpdating;
    $[19] = t8;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== canEditTripSelections || $[23] !== hasMultipleRooms || $[24] !== isTripUpdating || $[25] !== prefetchRooms || $[26] !== rooms || $[27] !== t) {
    let t12;
    if ($[29] !== canEditTripSelections || $[30] !== hasMultipleRooms || $[31] !== isTripUpdating || $[32] !== prefetchRooms || $[33] !== t) {
      t12 = (room, index) => <Fragment key={`traveller-mix-${index}`}><Divider variant="horizontal" /><ItineraryCard.ChangeField label={hasMultipleRooms ? t("room_number", {
          number: index + 1
        }) : t("accommodation_field_room")} value={room.displayName} onPress={canEditTripSelections ? () => {
          trackFunnelEvent("trip_funnel_edit_room", {
            room_number: index + 1
          });
          setRoomDrawerIndex(index);
        } : undefined} onPrefetch={prefetchRooms} isDisabled={isTripUpdating} /></Fragment>;
      $[29] = canEditTripSelections;
      $[30] = hasMultipleRooms;
      $[31] = isTripUpdating;
      $[32] = prefetchRooms;
      $[33] = t;
      $[34] = t12;
    } else {
      t12 = $[34];
    }
    t11 = rooms.map(t12);
    $[22] = canEditTripSelections;
    $[23] = hasMultipleRooms;
    $[24] = isTripUpdating;
    $[25] = prefetchRooms;
    $[26] = rooms;
    $[27] = t;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[35] !== t10 || $[36] !== t11) {
    t12 = <>{t10}{t11}</>;
    $[35] = t10;
    $[36] = t11;
    $[37] = t12;
  } else {
    t12 = $[37];
  }
  const changeFields = t12;
  let t13;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <StayPictogram size="small" decorative={true} />;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  const t14 = `Stay in ${placeTitle}`;
  let t15;
  if ($[39] !== t14) {
    t15 = <ItineraryCard.Header icon={t13} title={t14} />;
    $[39] = t14;
    $[40] = t15;
  } else {
    t15 = $[40];
  }
  let t16;
  if ($[41] !== images || $[42] !== isFavourite || $[43] !== t) {
    t16 = images.length > 0 ? <ItineraryCard.Gallery images={images} tag={isFavourite ? <ListingCard.Tag icon={<BywayFavorite decorative={true} size="small" />}>{t("byway_favourite")}</ListingCard.Tag> : undefined} /> : null;
    $[41] = images;
    $[42] = isFavourite;
    $[43] = t;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  let t17;
  if ($[45] !== accommodationTitle) {
    t17 = accommodationTitle ? <ItineraryCard.Subheading>{accommodationTitle}</ItineraryCard.Subheading> : null;
    $[45] = accommodationTitle;
    $[46] = t17;
  } else {
    t17 = $[46];
  }
  let t18;
  if ($[47] !== isStayUnavailable || $[48] !== tFeedback) {
    t18 = isStayUnavailable ? <ItineraryCard.InfoBanner variant="error">{tFeedback("stay_unavailable")}</ItineraryCard.InfoBanner> : null;
    $[47] = isStayUnavailable;
    $[48] = tFeedback;
    $[49] = t18;
  } else {
    t18 = $[49];
  }
  let t19;
  if ($[50] !== accommodationValue || $[51] !== changeFields) {
    t19 = accommodationValue ? <div className={styles.changeFieldsBox}>{changeFields}</div> : null;
    $[50] = accommodationValue;
    $[51] = changeFields;
    $[52] = t19;
  } else {
    t19 = $[52];
  }
  let t20;
  if ($[53] !== arrivalDistance || $[54] !== boardBases || $[55] !== departureDistance) {
    t20 = <StayMetaBlock arrivalDistance={arrivalDistance} departureDistance={departureDistance} boardBases={boardBases} />;
    $[53] = arrivalDistance;
    $[54] = boardBases;
    $[55] = departureDistance;
    $[56] = t20;
  } else {
    t20 = $[56];
  }
  const t21 = variant === "stay-expanded";
  let t22;
  if ($[57] !== bodyText || $[58] !== checkInOutDetails || $[59] !== t21 || $[60] !== websiteUrl) {
    t22 = <StayFold bodyText={bodyText} websiteUrl={websiteUrl} checkInOutDetails={checkInOutDetails} defaultExpanded={t21} />;
    $[57] = bodyText;
    $[58] = checkInOutDetails;
    $[59] = t21;
    $[60] = websiteUrl;
    $[61] = t22;
  } else {
    t22 = $[61];
  }
  let t23;
  if ($[62] !== t17 || $[63] !== t18 || $[64] !== t19 || $[65] !== t20 || $[66] !== t22) {
    t23 = <ItineraryCard.Content><div className={styles.expandedStay}>{t17}{t18}{t19}{t20}{t22}</div></ItineraryCard.Content>;
    $[62] = t17;
    $[63] = t18;
    $[64] = t19;
    $[65] = t20;
    $[66] = t22;
    $[67] = t23;
  } else {
    t23 = $[67];
  }
  let t24;
  if ($[68] !== prefetchRef || $[69] !== t15 || $[70] !== t16 || $[71] !== t23) {
    t24 = <ItineraryCard ref={prefetchRef}>{t15}{t16}{t23}</ItineraryCard>;
    $[68] = prefetchRef;
    $[69] = t15;
    $[70] = t16;
    $[71] = t23;
    $[72] = t24;
  } else {
    t24 = $[72];
  }
  let t25;
  if ($[73] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = () => setIsAlternativesOpen(false);
    $[73] = t25;
  } else {
    t25 = $[73];
  }
  const t26 = accommodationStay?.accommodation.distanceFromArrivalStationMeters;
  const t27 = accommodationStay?.accommodation.distanceFromDepartureStationMeters;
  let t28;
  if ($[74] !== t26 || $[75] !== t27) {
    t28 = {
      distanceFromArrivalStationMeters: t26,
      distanceFromDepartureStationMeters: t27
    };
    $[74] = t26;
    $[75] = t27;
    $[76] = t28;
  } else {
    t28 = $[76];
  }
  let t29;
  if ($[77] !== isAlternativesOpen || $[78] !== itemId || $[79] !== placeTitle || $[80] !== t28 || $[81] !== tripId) {
    t29 = <AlternativeAccommodationDrawer isOpen={isAlternativesOpen} close={t25} tripId={tripId} itemId={itemId} placeName={placeTitle} currentStationDistances={t28} />;
    $[77] = isAlternativesOpen;
    $[78] = itemId;
    $[79] = placeTitle;
    $[80] = t28;
    $[81] = tripId;
    $[82] = t29;
  } else {
    t29 = $[82];
  }
  const t30 = roomDrawerIndex !== null;
  let t31;
  if ($[83] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = () => setRoomDrawerIndex(null);
    $[83] = t31;
  } else {
    t31 = $[83];
  }
  const t32 = roomDrawerIndex ?? 0;
  let t33;
  if ($[84] !== itemId || $[85] !== placeTitle || $[86] !== t30 || $[87] !== t32 || $[88] !== tripId) {
    t33 = <ChangeRoomDrawer isOpen={t30} close={t31} tripId={tripId} itemId={itemId} roomIndex={t32} placeName={placeTitle} />;
    $[84] = itemId;
    $[85] = placeTitle;
    $[86] = t30;
    $[87] = t32;
    $[88] = tripId;
    $[89] = t33;
  } else {
    t33 = $[89];
  }
  let t34;
  if ($[90] !== t24 || $[91] !== t29 || $[92] !== t33) {
    t34 = <>{t24}{t29}{t33}</>;
    $[90] = t24;
    $[91] = t29;
    $[92] = t33;
    $[93] = t34;
  } else {
    t34 = $[93];
  }
  return t34;
}